import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState, type Ref } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { bordaPreviewSummary } from "@voting/borda";
import {
	PrimaryButton,
	SecondaryLink,
	SequenceNumeral,
	Card,
	RevealBar,
	REVEAL_BAR_DURATION_MS,
	REVEAL_STAGGER_MS,
	VotingMechanicsDemo,
} from "../components/ui";
import { PersonaPicker } from "../components/PersonaPicker";
import { TurnstileGate } from "../components/TurnstileGate";
import { useStaggeredEntrance } from "../hooks/useStaggeredEntrance";
import {
	canSubmitCreateRoom,
	CREATE_ROOM_CLIENT_MESSAGES,
	getCreateRoomBlockMessage,
	MAX_POLL_OPTIONS,
	nonEmptyCreateOptions,
	parseLaunchSeedParams,
	prefillQuestionFromSeedSummary,
} from "../lib/roomCreateValidation";
import {
	getPersonaPreference,
	type ResultsPersonaId,
	voterPathWithPersona,
} from "../lib/personaPreference";
import { friendlyApiErrorPayload } from "../lib/userFacingErrors";
import { webCreateCopy } from "../lib/webPersonaCopy";
import {
	isCreateRoomTurnstileRequired,
	isTurnstileMisconfigured,
	parseHealthTurnstileFields,
	resolveTurnstileSiteKey,
} from "../lib/turnstileClientConfig";
import { truncateUuidsForDisplay } from "../lib/truncateUuids";

const BUILD_TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "").trim();

const HERO_DEMO_BARS = [
	{ label: "Tuesday 2pm", value: 100, isWinner: true },
	{ label: "Monday 9am", value: 33, isWinner: false },
	{ label: "Thursday 1pm", value: 22, isWinner: false },
] as const;

/** Home hero-only RevealBar demo loop (idle-motion exception). */
function HomeHeroDemo() {
	const reduceMotion = useReducedMotion() === true;
	const [cycle, setCycle] = useState(0);
	const [resetting, setResetting] = useState(false);
	const [showSummary, setShowSummary] = useState(reduceMotion);

	useEffect(() => {
		if (reduceMotion) {
			setShowSummary(true);
			setResetting(false);
			return;
		}
		setResetting(false);
		setShowSummary(false);
		const summaryAt = 2 * REVEAL_STAGGER_MS + REVEAL_BAR_DURATION_MS + 200;
		const summaryT = window.setTimeout(() => setShowSummary(true), summaryAt);
		const resetT = window.setTimeout(() => setResetting(true), 5000);
		const replayT = window.setTimeout(() => {
			setShowSummary(false);
			setCycle((c) => c + 1);
		}, 5400);
		return () => {
			window.clearTimeout(summaryT);
			window.clearTimeout(resetT);
			window.clearTimeout(replayT);
		};
	}, [cycle, reduceMotion]);

	return (
		<div className="flex flex-col justify-center bg-wi-bg-band px-8 py-10 md:px-8 md:py-10">
			<p className="wi-type-caption font-medium uppercase tracking-[0.06em] text-wi-gold">Live example</p>
			<p className="mt-sm font-medium text-body text-wi-text">when can we meet?</p>
			<div className="mt-md" key={cycle}>
				{HERO_DEMO_BARS.map((bar, i) => (
					<RevealBar
						key={bar.label}
						label={bar.label}
						value={resetting ? 0 : bar.value}
						isWinner={!resetting && bar.isWinner}
						delayMs={reduceMotion || resetting ? 0 : i * REVEAL_STAGGER_MS}
						animate={!reduceMotion && !resetting}
					/>
				))}
			</div>
			{showSummary && !resetting ? (
				<p className="mt-sm text-body-sm text-wi-signal">Tuesday 2pm is clearly ahead.</p>
			) : (
				<p className="mt-sm min-h-[1.25rem] text-body-sm text-transparent" aria-hidden>
					—
				</p>
			)}
		</div>
	);
}

type CreateRoomResponse = {
	roomId: string;
	voterPath: string;
	curatorPath: string;
	expiresAt: number;
};

const TTL_PRESETS = [
	{ label: "2 hours", hours: 2 },
	{ label: "4 hours", hours: 4 },
	{ label: "24 hours", hours: 24 },
	{ label: "48 hours", hours: 48 },
] as const;

type OptionPreset = {
	label: string;
	hint: string;
	options: string[];
};

const BUSINESS_HOUR_SLOTS = [
	"Monday 9:00 AM",
	"Monday 11:00 AM",
	"Tuesday 2:00 PM",
	"Wednesday 10:00 AM",
	"Thursday 1:00 PM",
	"Friday 3:00 PM",
] as const;

const EVENING_WEEKEND_SLOTS = [
	"Monday 6:00 PM",
	"Tuesday 7:30 PM",
	"Thursday 6:00 PM",
	"Friday 6:30 PM",
	"Saturday 10:00 AM",
	"Sunday 4:00 PM",
] as const;

const OPTION_PRESETS: readonly OptionPreset[] = [
	{
		label: "Business hours",
		hint: "Six weekday meeting slots.",
		options: [...BUSINESS_HOUR_SLOTS],
	},
	{
		label: "Evenings / weekends",
		hint: "Six after-hours slots.",
		options: [...EVENING_WEEKEND_SLOTS],
	},
	{
		label: "Weekdays",
		hint: "Use when date matters more than time.",
		options: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
	},
	{
		label: "Next priorities",
		hint: "A balanced work-planning set.",
		options: ["Customer discovery", "Product polish", "Ops hardening", "Sales follow-up", "Pricing tests"],
	},
] as const;

function displayShareUrl(path: string): string {
	const absolute =
		typeof window !== "undefined" ? `${window.location.origin}${path}` : path;
	return truncateUuidsForDisplay(absolute);
}

	export function HomePage() {
		const navigate = useNavigate();
		const prefersReducedMotion = useReducedMotion() === true;
		const h1Entrance = useStaggeredEntrance("h1");
		const bodyEntrance = useStaggeredEntrance("body");
		const ctaEntrance = useStaggeredEntrance("cta");
		const { ref: howRef, ...howEntrance } = useStaggeredEntrance("belowFold");
		const [searchParams] = useSearchParams();
	const { seedIssueKey, seedIssueSummary } = parseLaunchSeedParams(searchParams);
	const ideaInputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const [formErr, setFormErr] = useState<string | null>(null);
	const [creating, setCreating] = useState(false);
	const [issued, setIssued] = useState<CreateRoomResponse | null>(null);
	const [openingCurator, setOpeningCurator] = useState(false);
	const [copyMsg, setCopyMsg] = useState<string | null>(null);
	const [handoffCopied, setHandoffCopied] = useState(false);

	const [question, setQuestion] = useState("");
	const [options, setOptions] = useState<string[]>(["", ""]);
	const [votingType, setVotingType] = useState<"ranked" | "quadratic">("ranked");
	const [ttlHours, setTtlHours] = useState<number>(48);
	const [personaId, setPersonaId] = useState<ResultsPersonaId>(() => getPersonaPreference());
	const [masterKeyAck, setMasterKeyAck] = useState(false);
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const [turnstileResetKey, setTurnstileResetKey] = useState(0);
	const [serverTurnstileRequired, setServerTurnstileRequired] = useState<boolean | null>(null);
	const [runtimeTurnstileSiteKey, setRuntimeTurnstileSiteKey] = useState("");

	useEffect(() => {
		if (!seedIssueSummary) return;
		setQuestion((prev) => prefillQuestionFromSeedSummary(prev, seedIssueSummary));
	}, [seedIssueSummary]);

	useEffect(() => {
		let cancelled = false;
		void fetch("/api/health")
			.then((res) => (res.ok ? res.json() : null))
			.then((data: unknown) => {
				if (cancelled) return;
				const { serverRequired, runtimeSiteKey } = parseHealthTurnstileFields(data);
				if (serverRequired !== null) setServerTurnstileRequired(serverRequired);
				if (runtimeSiteKey) setRuntimeTurnstileSiteKey(runtimeSiteKey);
			})
			.catch(() => {
				/* health optional — fall back to build-time site key only */
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const turnstileSiteKey = resolveTurnstileSiteKey(BUILD_TURNSTILE_SITE_KEY, runtimeTurnstileSiteKey);
	const hasTurnstileWidget = turnstileSiteKey.length > 0;
	const turnstileRequired = isCreateRoomTurnstileRequired(BUILD_TURNSTILE_SITE_KEY, serverTurnstileRequired);
	const turnstileMisconfigured = isTurnstileMisconfigured(turnstileRequired, turnstileSiteKey);

	const filled = useMemo(() => nonEmptyCreateOptions(options), [options]);
	const bordaHelp = votingType === "ranked" && filled.length >= 2 ? bordaPreviewSummary(filled.length) : null;
	const createCopy = useMemo(() => webCreateCopy(personaId, "web-create"), [personaId]);

	const createFormFields = useMemo(
		() => ({
			question,
			options,
			masterKeyAck,
			turnstileToken,
			turnstileRequired,
			creating,
		}),
		[question, options, masterKeyAck, turnstileToken, turnstileRequired, creating],
	);

	const canIssue = canSubmitCreateRoom(createFormFields) && !turnstileMisconfigured;

	const panelMotion = prefersReducedMotion
		? { initial: false as const, animate: { opacity: 1 }, exit: { opacity: 1 }, transition: { duration: 0 } }
		: {
				initial: { opacity: 0, y: 10 },
				animate: { opacity: 1, y: 0 },
				exit: { opacity: 0, y: -8 },
				transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const },
			};

	const setOption = (i: number, v: string) => {
		setOptions((prev) => {
			const next = [...prev];
			next[i] = v;
			return next;
		});
	};

	const addOptionWithFocus = useCallback(() => {
		setOptions((prev) => {
			if (prev.length >= MAX_POLL_OPTIONS) return prev;
			const newIndex = prev.length;
			window.setTimeout(() => {
				ideaInputRefs.current[newIndex]?.focus();
			}, 0);
			return [...prev, ""];
		});
	}, []);

	const removeOption = (i: number) => {
		setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, j) => j !== i)));
	};

	const applyOptionPreset = (preset: OptionPreset) => {
		setOptions(preset.options);
		setFormErr(null);
	};

	async function copyText(label: string, text: string, opts?: { handoff?: boolean }) {
		try {
			await navigator.clipboard.writeText(text);
			if (opts?.handoff) {
				setHandoffCopied(true);
				setCopyMsg("Saved to your clipboard — guard it well");
				window.setTimeout(() => {
					setHandoffCopied(false);
					setCopyMsg(null);
				}, 2200);
			} else {
				setCopyMsg(`${label} copied`);
				window.setTimeout(() => setCopyMsg(null), 2000);
			}
		} catch {
			setCopyMsg("Copy didn’t work — select the link and copy manually.");
			setHandoffCopied(false);
		}
	}

	return (
		<div className="space-y-xl pb-2xl">
			{seedIssueKey ? (
				<div className="rounded-wi-card border border-wi-gold/25 bg-wi-gold/8 px-4 py-3 text-body-sm text-wi-text/85">
					From Jira issue <span className="font-medium text-wi-text">{seedIssueKey}</span>
					{seedIssueSummary
						? " — we prefilled the question from the ticket title. Review the options below, then launch when you are ready."
						: " — add your options below, then launch when you are ready. After you finalize, commit results back to this issue."}
				</div>
			) : null}

				{/* §4.1 Two-column hero + RevealBar demo */}
				<section className="grid grid-cols-1 gap-0 overflow-hidden rounded-wi-card md:grid-cols-[1.1fr_0.9fr]">
					<div className="flex flex-col justify-center space-y-md px-6 py-10 md:px-10 md:py-14">
						<motion.h1 className="wi-type-display text-wi-text" {...h1Entrance}>
							Your group can&apos;t agree.
							<br />
							<span className="text-wi-gold">Fix that</span> in 30 seconds.
						</motion.h1>
						<motion.p className="max-w-[380px] wi-type-body text-wi-text-secondary" {...bodyEntrance}>
							No account. No trackers. Just a fair answer everyone can see the math behind.
						</motion.p>
						{issued ? null : (
							<motion.div className="wi-cta-row pt-xs" {...ctaEntrance}>
								<PrimaryButton
									type="button"
									onClick={() => {
										document.getElementById("create")?.scrollIntoView({ behavior: "smooth", block: "start" });
									}}
								>
									Start a poll
								</PrimaryButton>
								<SecondaryLink href="#how-it-works">See how it works</SecondaryLink>
							</motion.div>
						)}
					</div>
					<HomeHeroDemo />
				</section>

				{/* How it works — serif-numbered steps, no cards */}
				<motion.section
					id="how-it-works"
					ref={howRef as Ref<HTMLElement>}
					className="scroll-mt-8 space-y-md pt-xl"
					{...howEntrance}
				>
					<h2 className="wi-type-h1 text-wi-text">How it works</h2>
					<ol className="grid gap-y-7" style={{ columnGap: 20 }}>
						<li className="flex gap-5">
							<SequenceNumeral>01</SequenceNumeral>
							<div className="space-y-xs pt-0.5">
								<p className="font-display text-h2 text-wi-text">Gather ideas</p>
								<p className="wi-type-body-sm text-wi-text-secondary">
									Drop a few options in. Keep it short enough that people will actually vote.
								</p>
							</div>
						</li>
						<li className="flex gap-5">
							<SequenceNumeral>02</SequenceNumeral>
							<div className="space-y-xs pt-0.5">
									<p className="font-display text-h2 text-wi-text">Vote fairly</p>
									<p className="wi-type-body-sm text-wi-text-secondary">
										Everyone ranks the options or shares a points budget — so intensity counts, but one person can’t
										drown out the room.
									</p>
							</div>
						</li>
						<li className="flex gap-5">
							<SequenceNumeral>03</SequenceNumeral>
							<div className="space-y-xs pt-0.5">
								<p className="font-display text-h2 text-wi-text">Decide, on record</p>
								<p className="wi-type-body-sm text-wi-text-secondary">
									The Curator finalizes it. Everyone sees the same outcome, and why.
								</p>
							</div>
						</li>
					</ol>
					<VotingMechanicsDemo compact className="mt-md" />
				</motion.section>

				{/* Trust strip — plain-language, no card wrappers */}
				<section className="space-y-md border-wi-border border-t pt-xl">
					<h2 className="wi-type-h2 text-wi-text">Why you can trust it</h2>
					<ul className="space-y-sm">
						<li className="wi-type-body-sm text-wi-text-secondary">
							<strong className="font-medium text-wi-text">Nothing lingers.</strong> Polls expire on your
							schedule — there’s no permanent voter registry sitting around afterward.
						</li>
						<li className="wi-type-body-sm text-wi-text-secondary">
							<strong className="font-medium text-wi-text">Nothing leaks.</strong> No ads, no trackers, no
							reselling what people tell you.
						</li>
							<li className="wi-type-body-sm text-wi-text-secondary">
								<strong className="font-medium text-wi-text">Nobody can stack the vote.</strong> Ranked choice and
								weighted picks (quadratic voting) keep intensity visible without letting one voice outweigh the room.
							</li>
					</ul>
					<p className="wi-type-caption text-wi-text-muted">
						Curious how we built it this way?{" "}
						<Link to="/security" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							Read our security notes →
						</Link>
					</p>
				</section>

			<div id="create" className="scroll-mt-8">
				<AnimatePresence mode="wait">
						{issued ? (
							<motion.div
								key="issued"
								className="space-y-md border-t border-wi-text/[0.09] pt-lg"
								{...panelMotion}
							>
								<div className="space-y-sm">
									<h2 className="wi-type-h1 text-wi-text">Nice — your poll is live.</h2>
									<p className="wi-type-body-sm text-wi-text/75">
										The Curator is whoever sets up and finalizes the poll. Keep your Curator Link private —
										anyone with it can control this poll. Treat it like a key.{" "}
										<Link
											to="/help/running-a-session"
											className="font-medium text-wi-gold underline-offset-2 hover:underline"
										>
											Running a session guide
										</Link>
									</p>
									<p className="wi-type-body-sm text-wi-text/65">
										Ballots and metadata stay in our regional D1 store for the room’s lifetime so you can
										recover with your Curator link. Polls expire on your schedule.
									</p>
								</div>

									<div className="wi-card space-y-xs p-4">
										<p className="wi-type-caption font-medium uppercase tracking-wide text-wi-text-muted">
											Invitation (voters)
										</p>
										<div className="flex flex-wrap gap-2">
											<code
												className="max-w-full min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-xl border border-wi-border bg-wi-bg px-3 py-2.5 text-caption leading-relaxed"
												title={
													typeof window !== "undefined"
														? `${window.location.origin}${issued.voterPath}`
														: issued.voterPath
												}
											>
												{displayShareUrl(issued.voterPath)}
											</code>
											<button
												type="button"
												className="min-h-11 rounded-xl border border-wi-border px-5 py-3 text-body-sm transition hover:border-wi-gold/35 hover:bg-wi-bg/50"
												onClick={() =>
													void copyText("Invitation", `${window.location.origin}${issued.voterPath}`)
												}
											>
												Copy
											</button>
										</div>
									</div>

									{/* The Handoff — Curator Link with gold-soft pulse (PRD §3.3) */}
									<Card className="space-y-xs !p-4" interactive>
										<p className="wi-type-caption font-medium uppercase tracking-wide text-wi-text-muted">
											Curator Link (private)
										</p>
										<div className="flex flex-wrap gap-2">
											<code
												className={`wi-handoff-pulse max-w-full min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-xl border border-wi-border bg-wi-bg px-3 py-2.5 text-caption leading-relaxed ${prefersReducedMotion ? "" : ""}`}
												title={
													typeof window !== "undefined"
														? `${window.location.origin}${issued.curatorPath}`
														: issued.curatorPath
												}
											>
												{displayShareUrl(issued.curatorPath)}
											</code>
											<button
												type="button"
												className="min-h-11 rounded-xl border border-wi-border px-5 py-3 text-body-sm transition hover:border-wi-gold/35 hover:bg-wi-bg/50"
												onClick={() =>
													void copyText(
														"Curator link",
														`${window.location.origin}${issued.curatorPath}`,
														{ handoff: true },
													)
												}
											>
												Copy
											</button>
										</div>
									</Card>

								{copyMsg ? (
									<p className="flex items-center gap-2 wi-type-body-sm text-wi-text/80" role="status">
										{handoffCopied ? (
											<motion.span
												aria-hidden
												className="inline-flex text-wi-signal"
												initial={prefersReducedMotion ? false : { scale: 0 }}
												animate={{ scale: 1 }}
												transition={
													prefersReducedMotion
														? { duration: 0 }
														: { type: "spring", stiffness: 420, damping: 18, duration: 0.2 }
												}
											>
												<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
													<path d="M3.5 8.5 6.5 11.5 12.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
											</motion.span>
										) : null}
										{copyMsg}
									</p>
								) : null}

								<div className="wi-cta-row pt-xs">
									<PrimaryButton
										type="button"
										onClick={() =>
											void copyText(
												"Curator link",
												`${window.location.origin}${issued.curatorPath}`,
												{ handoff: true },
											)
										}
									>
										Copy Curator Link
									</PrimaryButton>
									<SecondaryLink
										arrow={false}
										type="button"
										onClick={() => {
											setIssued(null);
											setOpeningCurator(false);
											setMasterKeyAck(false);
											setTurnstileToken(null);
											setHandoffCopied(false);
											setCopyMsg(null);
											setTurnstileResetKey((k) => k + 1);
										}}
									>
										Start another poll
									</SecondaryLink>
								</div>
								<p className="wi-type-caption text-wi-text/50">
									<button
										type="button"
										disabled={openingCurator}
										className="font-medium text-wi-gold underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
										onClick={() => {
											setOpeningCurator(true);
											navigate(issued.curatorPath);
										}}
									>
										{openingCurator ? "Opening…" : "Open curator workspace →"}
									</button>
								</p>
							</motion.div>
							) : (
								<motion.form
									key="form"
									className="border-wi-border border-t pt-xl"
									{...panelMotion}
								onSubmit={async (e: FormEvent) => {
									e.preventDefault();
									const blockMsg = getCreateRoomBlockMessage(createFormFields);
									if (blockMsg) {
										setFormErr(blockMsg);
										return;
									}
									if (turnstileMisconfigured) {
										setFormErr(CREATE_ROOM_CLIENT_MESSAGES.turnstileMisconfigured);
										return;
									}
									const tokenForCreate = turnstileToken?.trim() || null;
									setCreating(true);
									setFormErr(null);
									try {
										const res = await fetch("/api/rooms", {
											method: "POST",
											headers: { "Content-Type": "application/json" },
											body: JSON.stringify({
												question: question.trim(),
												votingType,
												options: filled,
												ttlHours,
												resultsPersona: personaId,
												...(tokenForCreate ? { turnstileToken: tokenForCreate } : {}),
											}),
										});
										const data = (await res.json()) as CreateRoomResponse & {
											error?: string;
											message?: string;
											retryAfterSec?: number;
										};
										if (!res.ok) {
											throw new Error(
												friendlyApiErrorPayload(
													data as unknown,
													"We couldn’t start this poll. Check your connection and try again.",
												),
											);
										}
										setIssued({
											...data,
											voterPath: voterPathWithPersona(data.roomId, personaId),
										});
										setTurnstileToken(null);
										setTurnstileResetKey((k) => k + 1);
									} catch (err) {
										const msg =
											err instanceof Error
												? err.message
												: "We couldn’t start this poll. Check your connection and try again.";
										setFormErr(
											turnstileMisconfigured
												? CREATE_ROOM_CLIENT_MESSAGES.turnstileMisconfigured
												: msg,
										);
									} finally {
										setCreating(false);
									}
								}}
							>
								<Card className="space-y-md">
									<h2 className="wi-type-h1 text-wi-text">{createCopy.title}</h2>

								<label className="block space-y-xs">
									<span className="wi-type-body-sm font-medium text-wi-text">What is the question?</span>
									<textarea
										className="w-full rounded-xl border border-wi-text/12 bg-wi-surface px-3.5 py-2.5 wi-type-body-sm outline-none ring-wi-gold/25 transition focus:ring-2"
										rows={3}
										placeholder="What are we deciding?"
										value={question}
										onChange={(e) => setQuestion(e.target.value)}
										required
									/>
								</label>

								<div className="space-y-sm">
									<div className="flex flex-wrap items-end justify-between gap-2">
										<div>
											<span className="wi-type-body-sm font-medium text-wi-text">Options (2–15)</span>
											<p className="mt-0.5 wi-type-caption leading-snug text-wi-text/48">
												Use a preset or edit every row by hand.{" "}
												<span className="whitespace-nowrap">Ctrl + Enter</span> or{" "}
												<span className="whitespace-nowrap">⌘ + Enter</span> adds a row.
											</p>
										</div>
										<button
											type="button"
											className="wi-type-caption font-medium text-wi-gold underline-offset-2 hover:underline disabled:opacity-40"
											disabled={options.length >= MAX_POLL_OPTIONS}
											onClick={addOptionWithFocus}
										>
											Add row
										</button>
									</div>
									<div className="space-y-sm">
										<div>
											<p className="wi-type-caption font-medium uppercase tracking-wide text-wi-text/70">
												Quick fill options
											</p>
											<p className="mt-1 wi-type-caption leading-snug text-wi-text/48">
												Replace the option list with a helpful editable set, without changing the question.
											</p>
										</div>
										<div className="grid gap-2 sm:grid-cols-2">
											{OPTION_PRESETS.map((preset) => (
												<button
													key={preset.label}
													type="button"
													className="rounded-lg border border-wi-text/10 bg-transparent px-3 py-2 text-left transition hover:border-wi-gold/30 hover:bg-wi-bg/40"
													onClick={() => applyOptionPreset(preset)}
												>
													<span className="block wi-type-body-sm font-medium text-wi-text">
														{preset.label}
													</span>
													<span className="mt-0.5 block wi-type-caption leading-snug text-wi-text/50">
														{preset.hint}
													</span>
												</button>
											))}
										</div>
									</div>
									<div className="space-y-2">
										{options.map((opt, i) => (
											<div key={i} className="flex gap-2">
												<input
													ref={(el) => {
														ideaInputRefs.current[i] = el;
													}}
													className="min-w-0 flex-1 rounded-xl border border-wi-text/10 bg-wi-surface px-3.5 py-2.5 wi-type-body-sm italic text-wi-text/80 outline-none transition placeholder:text-wi-text/35 focus:border-wi-gold/35 focus:not-italic focus:text-wi-text"
													placeholder={i < 2 ? `Option ${i + 1} (required)` : `Option ${i + 1}`}
													value={opt}
													onChange={(e) => setOption(i, e.target.value)}
													onKeyDown={(e) => {
														if (e.key !== "Enter" || !(e.ctrlKey || e.metaKey)) return;
														e.preventDefault();
														addOptionWithFocus();
													}}
												/>
												<button
													type="button"
													className="min-h-11 min-w-11 shrink-0 rounded-xl border border-wi-text/10 px-3 py-2.5 wi-type-caption text-wi-text/50 transition hover:border-wi-text/25 hover:text-wi-text disabled:opacity-30"
													disabled={options.length <= 2}
													onClick={() => removeOption(i)}
													aria-label={`Remove option ${i + 1}`}
												>
													Remove
												</button>
											</div>
										))}
										{options.length < MAX_POLL_OPTIONS ? (
											<button
												type="button"
												onClick={addOptionWithFocus}
												className="flex min-h-11 w-full items-center gap-2 rounded-xl border border-dashed border-wi-text/18 bg-transparent px-3.5 py-3 text-left wi-type-body-sm text-wi-text/50 transition hover:border-wi-gold/30 hover:bg-wi-bg/40 hover:text-wi-text/75"
											>
												<span className="font-mono text-caption opacity-60">+</span>
												<span className="italic">Add another option (optional, up to 15)</span>
											</button>
										) : null}
									</div>
									{/* Momentum — after 2nd option (PRD §3.3) */}
									{filled.length >= 2 ? (
										<motion.p
											className="wi-type-caption text-wi-text/55"
											initial={prefersReducedMotion ? false : { opacity: 0 }}
											animate={{ opacity: 1 }}
											transition={{ duration: prefersReducedMotion ? 0 : 0.25 }}
											aria-live="polite"
										>
											{filled.length} of {MAX_POLL_OPTIONS} — plenty of room
										</motion.p>
									) : null}
								</div>

									<details className="group border-wi-border border-t pt-sm">
										<summary className="cursor-pointer list-none py-sm wi-type-body-sm font-medium text-wi-text marker:content-none [&::-webkit-details-marker]:hidden">
											<span className="inline-flex items-center gap-2">
												<span className="text-wi-text-muted transition group-open:rotate-90">›</span>
												Poll settings
											</span>
											<span className="mt-1 block font-normal wi-type-caption text-wi-text-muted">
												Voting style and how long the poll stays open
											</span>
										</summary>
										<div className="grid gap-md pb-sm sm:grid-cols-2">
											<label className="block space-y-xs">
												<span className="wi-type-body-sm font-medium text-wi-text">Voting style</span>
												<select
													className="w-full rounded-xl border border-wi-border bg-wi-surface px-3 py-2.5 wi-type-body-sm outline-none ring-wi-gold/25 focus:ring-2"
													value={votingType}
													onChange={(e) => setVotingType(e.target.value as "ranked" | "quadratic")}
												>
													<option value="ranked">Ranked choice</option>
													<option value="quadratic">Weighted picks (quadratic voting)</option>
												</select>
											</label>
											<label className="block space-y-xs">
												<span className="wi-type-body-sm font-medium text-wi-text">Time limit</span>
												<select
													className="w-full rounded-xl border border-wi-border bg-wi-surface px-3 py-2.5 wi-type-body-sm outline-none ring-wi-gold/25 focus:ring-2"
													value={ttlHours}
													onChange={(e) => setTtlHours(Number(e.target.value))}
												>
													{TTL_PRESETS.map((p) => (
														<option key={p.hours} value={p.hours}>
															{p.label}
														</option>
													))}
												</select>
											</label>
										</div>
									</details>

									<PersonaPicker
										value={personaId}
										onChange={setPersonaId}
										embedded
										hint={createCopy.personaHint}
									/>

								{bordaHelp ? (
									<p className="wi-type-caption leading-relaxed text-wi-text/80">
										<strong className="font-medium text-wi-text">Logic ledger:</strong> {bordaHelp}
									</p>
								) : null}

								<div className="space-y-sm border-t border-wi-text/[0.09] pt-md">
									<p className="wi-type-body-sm text-wi-text/75">
										The Curator is whoever sets up and finalizes the poll. Anyone with the Curator Link can
										control the poll — treat it like a key.
									</p>
									<label className="flex cursor-pointer gap-3 wi-type-body-sm leading-relaxed">
										<input
											type="checkbox"
											className="mt-1 size-4 shrink-0 rounded border-wi-text/28 accent-wi-gold"
											checked={masterKeyAck}
											onChange={(e) => setMasterKeyAck(e.target.checked)}
										/>
										<span className="text-wi-text">
											I understand the Curator Link is sensitive and I’ll store it safely.
										</span>
									</label>
								</div>

								{turnstileMisconfigured ? (
									<p className="rounded-xl border border-wi-gold/30 bg-wi-gold-soft px-3.5 py-2.5 wi-type-body-sm leading-relaxed text-wi-text">
										{CREATE_ROOM_CLIENT_MESSAGES.turnstileMisconfigured}
									</p>
								) : null}

								{hasTurnstileWidget && turnstileRequired ? (
									<div className="space-y-sm py-xs">
										<p className="wi-type-body-sm font-medium text-wi-text">
											Quick check — helps us keep polls fair.
										</p>
										<TurnstileGate
											siteKey={turnstileSiteKey}
											disabled={creating}
											resetKey={turnstileResetKey}
											onToken={setTurnstileToken}
										/>
									</div>
								) : turnstileRequired && serverTurnstileRequired === null ? (
									<div
										className="min-h-[65px] rounded-xl border border-wi-text/10 bg-wi-bg/40 p-4 wi-type-body-sm text-wi-text/70"
										aria-busy="true"
									>
										Loading verification…
									</div>
								) : null}

								{formErr ? (
									<p className="wi-type-body-sm text-wi-gold" role="alert">
										{formErr}
									</p>
								) : null}

									<div className="wi-cta-row">
										<PrimaryButton type="submit" disabled={!canIssue}>
											{creating ? "Starting poll…" : createCopy.submitLabel}
										</PrimaryButton>
									</div>
								</Card>
								</motion.form>
							)}
					</AnimatePresence>
				</div>

				<p className="border-t border-wi-text/[0.09] pt-lg wi-type-body-sm leading-relaxed text-wi-text/62">
					Exploring WorthyIdea for a team or workshop?{" "}
					<Link to="/membership" className="font-medium text-wi-gold underline-offset-2 hover:underline">
						Check out our Memberships
					</Link>
					.
				</p>
		</div>
	);
}
